import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Areas } from '@thive/fddm-types';
import { Stack } from '@mui/system';
import { Divider } from '@mui/material';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { JitRoutingPointProperties } from '@/modules/jitRoutingPoints/components/JitRoutingPointProperties';

import { LaneDirection } from './highway/LaneDirection';
import { HighwayConnectNeighbors } from './highway/HighwayConnectNeighbors';
import { HighwayConnectUturns } from './highway/HighwayConnectUturns';
import { HighwaySpacing } from './highway/HighwaySpacing';
import { VehicleLimit } from './highway/VehicleLimit';
import { Thickness } from './highway/Thickness';
import { MultiVehicleType } from './common/VehicleType';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedHighwayAreas: Areas['highwayAreas'];
}

function HighwayAreaParametersComponent({ selectedHighwayAreas }: Props) {
  const { t } = useTranslation();
  const title =
    selectedHighwayAreas.length === 1
      ? t<string>('interface:properties.highway.title', 'Highways Area')
      : t<string>(
          'interface:properties.highway.title_plural',
          `Highway areas (${selectedHighwayAreas.length})`,
          { amount: selectedHighwayAreas.length },
        );

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_HIGHWAY}
      title={title}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <Thickness selectedHighwayAreas={selectedHighwayAreas} />

        <PropertiesLabel i18nkey='interface:properties.highway.lane_direction'>
          <LaneDirection selectedHighways={selectedHighwayAreas} />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.highway.connect_neighbors'>
          <HighwayConnectNeighbors selectedHighwayAreas={selectedHighwayAreas} />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.highway.connect_uturns'>
          <HighwayConnectUturns selectedHighwayAreas={selectedHighwayAreas} />
        </PropertiesLabel>
        <HighwaySpacing selectedHighways={selectedHighwayAreas} />

        {/* <HighwayPoiPreferences selectedHighwayArea={selectedHighwayArea} /> for now, we hide this, this will be added back in the future. */}
        <Accordion
          persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_HIGHWAY_VEHICLE_SETTINGS}
          titleKey='interface:properties.sections.vehicle_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <MultiVehicleType selectedHighways={selectedHighwayAreas} />
            <VehicleLimit selectedHighways={selectedHighwayAreas} />
          </Stack>
        </Accordion>
      </Stack>
      <Divider />
      <JitRoutingPointProperties
        selectedHighway={selectedHighwayAreas[0]}
        isMultiSelection={selectedHighwayAreas.length > 1}
      />
    </Accordion>
  );
}

export const HighwayAreaParameters = memo(HighwayAreaParametersComponent);
